import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { guardianLinkApi } from "@bw/shared/api";
import type {
  ChildGuardianshipResponse,
  GuardianActivityRequestCreate,
  GuardianActivityRequestView,
  GuardianControlName,
  GuardianControlState,
  GuardianControlView,
  GuardianLinkResponse,
  GuardianPermissionView,
  MyGuardianControlsResponse,
} from "@bw/shared/types/generated";
import { queryKeys } from "./queryClient";
import { useMyProfile } from "./useMyProfile";

/**
 * Guardian-link hooks — parent-managed under-13 consent (COPPA, #1309 Part A).
 *
 * These live in `common/query` (NOT inside a feature module) because TWO sibling
 * modules consume them and a player app forbids cross-module imports: the
 * `onboarding` module's AgeOutcome reads the CHILD status + requests a link, and
 * the `family` module's approvals page reads + approves/revokes as the PARENT.
 * Both import this single common surface, mirroring how `useMyProfile` is shared
 * by `app/` and `onboarding`.
 *
 * The client is the SERVER's authority — these hooks add NO business logic. The
 * mutations surface the raw ApiError (GUARDIAN_LINK_SELF_NOT_ALLOWED,
 * GUARDIAN_LINK_ALREADY_EXISTS, GUARDIAN_LINK_NOT_FOUND, NOT_THE_PARENT,
 * CONSENT_EMAIL_NOT_VERIFIED, CHILD_NOT_PENDING, …) on `mutation.error` for the
 * page to branch on by `code` (via `GUARDIAN_LINK_ERROR_CODES` from `@bw/shared`)
 * — they never throw to an error boundary and never optimistically flip local
 * state.
 */

// ---------------------------------------------------------------------------
// CHILD — guardian-link status + request
// ---------------------------------------------------------------------------

/**
 * The CHILD's own guardian-link status (GET /users/me/guardian-link →
 * GuardianLinkResponse | null). SERVER-SCOPED to the token child (no uid in the
 * key). Read on mount by onboarding's AgeOutcome so a returning gated child sees
 * their pending/active link instead of an empty request form.
 *
 * @param enabled gate the fetch (default `true`). AgeOutcome only renders this
 *   in the pending branch, so it leaves it `true`.
 */
export function useGuardianLinkStatus(enabled = true) {
  return useQuery<GuardianLinkResponse | null>({
    queryKey: queryKeys.guardian.linkStatus(),
    enabled,
    queryFn: () => guardianLinkApi.getLinkStatus(),
  });
}

/**
 * The child requests a guardian link by parent email (POST /users/me/guardian-link).
 * On success the returned link IS the server's new state, so it seeds the
 * linkStatus cache slot directly (immediate pending view, no flash) AND the arm
 * is invalidated so a subsequent read re-confirms the server's truth. A failure
 * (GUARDIAN_LINK_SELF_NOT_ALLOWED / GUARDIAN_LINK_ALREADY_EXISTS /
 * CHILD_NOT_PENDING) stays on `mutation.error` for the panel to render inline.
 */
export function useRequestGuardianLink() {
  const qc = useQueryClient();
  return useMutation<GuardianLinkResponse, unknown, string>({
    mutationFn: (parentEmail: string) =>
      guardianLinkApi.requestGuardianLink(parentEmail),
    onSuccess: (link) => {
      qc.setQueryData(queryKeys.guardian.linkStatus(), link);
      void qc.invalidateQueries({
        queryKey: queryKeys.guardian.linkStatus(),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// PARENT — pending child requests + approve / revoke
// ---------------------------------------------------------------------------

/**
 * The PARENT's pending child-link requests (GET /users/me/child-approvals →
 * GuardianLinkResponse[]). SERVER-SCOPED to the token parent's verified email (no
 * uid in the key); each row carries `child_display_name` + `link_id` (the raw uid
 * is nulled server-side). Backs the Family approvals page.
 */
export function useChildApprovals() {
  return useQuery<GuardianLinkResponse[]>({
    queryKey: queryKeys.guardian.childApprovals(),
    queryFn: () => guardianLinkApi.listChildApprovals(),
  });
}

/**
 * The PARENT's APPROVED-children roster (GET /users/me/child-approvals?status=active
 * → GuardianLinkResponse[]) — the COPPA right-to-withdraw surface (#1630).
 * SERVER-SCOPED to the token parent's verified email (no uid in the key); each row
 * carries `child_display_name` + `link_id` + `consented_at` (the raw uid is nulled
 * server-side). Backs the "Approved children" section of the Family approvals page,
 * where each child can be REVOKED (re-gating them).
 */
export function useActiveChildren() {
  return useQuery<GuardianLinkResponse[]>({
    queryKey: queryKeys.guardian.activeChildren(),
    queryFn: () => guardianLinkApi.listActiveChildren(),
  });
}

/**
 * Invalidate BOTH parent rosters (pending + active). A guardian-link mutation
 * moves a link between the two lists — approve: pending → active; revoke: active →
 * (gone) — so each list must re-read the server's truth, never an optimistic
 * local splice.
 */
function invalidateParentRosters(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: queryKeys.guardian.childApprovals() });
  void qc.invalidateQueries({ queryKey: queryKeys.guardian.activeChildren() });
}

/**
 * The parent approves a pending link (POST /users/me/child-approvals/{id}/approve)
 * — the backend records the guardian waiver + activates the child. On success both
 * parent rosters are invalidated so each re-reads the server's truth (the approved
 * row leaves the pending list and enters the active roster) — never an optimistic
 * local splice. A failure (NOT_THE_PARENT / CONSENT_EMAIL_NOT_VERIFIED /
 * CHILD_NOT_PENDING / GUARDIAN_LINK_NOT_FOUND) stays on `mutation.error`.
 */
export function useApproveChild() {
  const qc = useQueryClient();
  return useMutation<GuardianLinkResponse, unknown, string>({
    mutationFn: (linkId: string) => guardianLinkApi.approveChild(linkId),
    onSuccess: () => invalidateParentRosters(qc),
  });
}

/**
 * The parent PAUSES a child (POST /users/me/child-approvals/{id}/revoke): the
 * account_active revoke (#2691); the parent's own link stays active. On success
 * both parent rosters are invalidated so each re-reads the server's truth (the
 * child stays on the roster, marked paused) — never an optimistic local edit.
 */
export function useRevokeChild() {
  const qc = useQueryClient();
  return useMutation<GuardianLinkResponse, unknown, string>({
    mutationFn: (linkId: string) => guardianLinkApi.revokeChild(linkId),
    onSuccess: () => invalidateParentRosters(qc),
  });
}

// ---------------------------------------------------------------------------
// GUARDIAN — one child's co-guardians and permissions (multi-guardian, #2691)
// ---------------------------------------------------------------------------
// Every call is keyed by the CALLER's own link id, never a child id.

/** GET /users/me/family/children/{linkId} — co-guardians, invites, permission state. */
export function useChildGuardianship(linkId: string) {
  return useQuery<ChildGuardianshipResponse>({
    queryKey: queryKeys.guardian.child(linkId),
    queryFn: () => guardianLinkApi.getChildGuardianship(linkId),
    enabled: Boolean(linkId),
  });
}

function invalidateChild(qc: ReturnType<typeof useQueryClient>, linkId: string): void {
  void qc.invalidateQueries({ queryKey: queryKeys.guardian.child(linkId) });
  invalidateParentRosters(qc);
}

/** Invite another adult as a co-guardian (409 GUARDIAN_LIMIT_REACHED at the cap). */
export function useInviteCoGuardian(linkId: string) {
  const qc = useQueryClient();
  return useMutation<GuardianLinkResponse, unknown, string>({
    mutationFn: (email: string) => guardianLinkApi.inviteCoGuardian(linkId, email),
    onSuccess: () => invalidateChild(qc, linkId),
  });
}

/** The caller stops being this child's guardian (only ever themselves). */
export function useLeaveGuardianship(linkId: string) {
  const qc = useQueryClient();
  return useMutation<GuardianLinkResponse, unknown, void>({
    mutationFn: () => guardianLinkApi.leaveGuardianship(linkId),
    onSuccess: () => invalidateChild(qc, linkId),
  });
}

/**
 * Turn account access on or off. A grant carries the version the guardian saw;
 * a co-guardian's change in between is 409 GUARDIAN_PERMISSION_CHANGED, which
 * the page surfaces as a "review the latest" prompt (never a silent retry). A
 * revoke always applies. Both re-read the server's state afterwards, including
 * after a conflict.
 */
export function useSetAccountActive(linkId: string) {
  const qc = useQueryClient();
  return useMutation<GuardianPermissionView, unknown, { on: boolean; version: number }>({
    mutationFn: ({ on, version }) =>
      on
        ? guardianLinkApi.grantPermission(linkId, "account_active", version)
        : guardianLinkApi.revokePermission(linkId, "account_active"),
    onSettled: () => invalidateChild(qc, linkId),
  });
}

// ---------------------------------------------------------------------------
// Guardian activity controls (#2691)
// ---------------------------------------------------------------------------

/**
 * The signed-in player's own effective controls (GET /users/me/guardian-controls).
 * Adults get `is_minor: false`; a minor with no guardian, or whose guardian paused
 * the account, is `look_only`. The server stays the authority: every controlled
 * action is refused there too, so this read only shapes what the app shows.
 */
export function useMyGuardianControls(enabled = true) {
  return useQuery<MyGuardianControlsResponse>({
    queryKey: queryKeys.guardian.myControls(),
    queryFn: () => guardianLinkApi.getMyControls(),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Is the signed-in player in look-only mode (#2691)? Only an under-13 child
 * (COPPA) can be: adults (a known date of birth, 18+) never read their
 * controls; a minor, an account with no date of birth, or one already waiting
 * for a guardian reads them once and the server decides. `paused` says a
 * guardian paused the account.
 */
export function useGuardianLookOnly(signedIn: boolean) {
  const profile = useMyProfile(signedIn);
  const gated = profile.data?.account_status === "pending_parental_consent";
  const maybeMinor = Boolean(profile.data) && (profile.data?.is_minor !== false || gated);
  const controls = useMyGuardianControls(signedIn && maybeMinor);
  // The server's controls answer wins once loaded (a paused account, or a
  // guardian who approved since the profile was read); the profile's gated
  // status only stands in when the controls read failed.
  const lookOnly = controls.data ? controls.data.look_only : gated;
  return {
    lookOnly,
    paused: lookOnly && controls.data?.reason === "paused",
    // Known: the profile has loaded (the app shell loads it first) and, for a
    // possible minor, the controls have settled. Until then callers show a
    // neutral state rather than guessing "waiting" vs "paused".
    known: !signedIn || (profile.isFetched && (!maybeMinor || controls.isFetched)),
    controls,
  };
}

/** The child asks a guardian (idempotent on the waiting request). */
export function useCreateGuardianRequest() {
  const qc = useQueryClient();
  return useMutation<GuardianActivityRequestView, unknown, GuardianActivityRequestCreate>({
    mutationFn: (data) => guardianLinkApi.createRequest(data),
    onSettled: () => void qc.invalidateQueries({ queryKey: queryKeys.guardian.myControls() }),
  });
}

/** Requests from every child the caller guards (newest first). */
export function useFamilyRequests(status: "pending" | "approved" | "all" = "all") {
  return useQuery<GuardianActivityRequestView[]>({
    queryKey: queryKeys.guardian.familyRequests(status),
    queryFn: () => guardianLinkApi.listFamilyRequests(status),
  });
}

/**
 * Approve or decline a pending request, or remove a standing approval. The first
 * guardian decision wins: a late one is 409 GUARDIAN_REQUEST_DECIDED naming who
 * decided. Every outcome re-reads the inbox and the child pages.
 */
export function useDecideFamilyRequest() {
  const qc = useQueryClient();
  return useMutation<
    GuardianActivityRequestView,
    unknown,
    { requestId: string; decision: "approve" | "decline" | "revoke" }
  >({
    mutationFn: ({ requestId, decision }) => guardianLinkApi.decideRequest(requestId, decision),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["guardian", "family-requests"] });
      void qc.invalidateQueries({ queryKey: ["guardian", "child"] });
    },
  });
}

/**
 * Change one activity control. A stricter choice always applies; a looser one
 * names the version the guardian saw, so a co-guardian's change in between is
 * 409 GUARDIAN_PERMISSION_CHANGED (shown as "review the latest"). Always re-reads.
 */
export function useSetGuardianControl(linkId: string) {
  const qc = useQueryClient();
  return useMutation<
    GuardianControlView,
    unknown,
    { control: GuardianControlName; state: GuardianControlState; version: number }
  >({
    mutationFn: ({ control, state, version }) =>
      guardianLinkApi.setControl(linkId, control, state, version),
    onSettled: () => {
      invalidateChild(qc, linkId);
      // An Ask that became Allow (or Decline) changes what Family approvals lists.
      void qc.invalidateQueries({ queryKey: ["guardian", "family-requests"] });
    },
  });
}
